import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import {
  AnthropicCard,
  ConfigCard,
  PromptsCard,
  Section,
  type Config,
  type PromptDef,
  type SecretStatus,
} from "../components/SettingsCards";

interface SettingsResponse {
  config: Config;
  secrets: { imap: SecretStatus; linkedin: SecretStatus; anthropic: SecretStatus };
}

interface PromptsResponse {
  prompts: PromptDef[];
}

interface User {
  username: string;
  email: string | null;
  enabled: boolean;
  status: string | null;
  created: string | null;
  is_admin: boolean;
}

interface UsersResponse {
  users: User[];
}

export function Admin() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const me = auth.user?.profile["cognito:username"] as string | undefined;
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<SettingsResponse>("/settings", token),
    enabled: !!token,
  });

  const saveConfig = useMutation({
    mutationFn: (c: Partial<Config>) =>
      api<{ ok: boolean }>("/settings", token, { method: "PUT", body: JSON.stringify(c) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  const saveSecret = useMutation({
    mutationFn: (args: { name: string; payload: Record<string, string | number> }) =>
      api<{ ok: boolean }>(`/settings/secret/${args.name}`, token, {
        method: "PUT",
        body: JSON.stringify(args.payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });

  const promptsQ = useQuery({
    queryKey: ["prompts"],
    queryFn: () => api<PromptsResponse>("/prompts", token),
    enabled: !!token,
  });

  const savePrompt = useMutation({
    mutationFn: (args: { name: string; content: string }) =>
      api<{ ok: boolean }>(`/prompts/${args.name}`, token, {
        method: "PUT",
        body: JSON.stringify({ content: args.content }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prompts"] }),
  });

  const usersQ = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api<UsersResponse>("/admin/users", token),
    enabled: !!token,
  });

  const invalidateUsers = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const action = useMutation({
    mutationFn: (args: { path: string; method: string }) =>
      api(args.path, token, { method: args.method }),
    onSuccess: invalidateUsers,
  });

  const createUser = useMutation({
    mutationFn: (args: { email: string; is_admin: boolean }) =>
      api("/admin/users", token, { method: "POST", body: JSON.stringify(args) }),
    onSuccess: invalidateUsers,
  });

  const [newEmail, setNewEmail] = useState("");
  const [newAdmin, setNewAdmin] = useState(false);

  const enc = (u: string) => encodeURIComponent(u);
  const busy = action.isPending || createUser.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-50">Admin</h1>
        <p className="mt-1 text-sm text-slate-400">Manage platform settings and user accounts.</p>
      </header>

      {/* ---- Platform settings ---- */}
      <div className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Platform settings</h2>
        {settingsQ.isPending && <p className="text-sm text-slate-500">Loading settings…</p>}
        {settingsQ.error && (
          <p className="text-sm text-rose-300">Error: {(settingsQ.error as Error).message}</p>
        )}
        {settingsQ.data && (
          <>
            <ConfigCard
              config={settingsQ.data.config}
              onSave={(c) => saveConfig.mutate(c)}
              saving={saveConfig.isPending}
            />
            <AnthropicCard
              status={settingsQ.data.secrets.anthropic}
              onSave={(p) => saveSecret.mutate({ name: "anthropic", payload: p })}
              saving={saveSecret.isPending}
            />
            <PromptsCard
              prompts={promptsQ.data?.prompts}
              loading={promptsQ.isPending}
              onSave={(name, content) => savePrompt.mutate({ name, content })}
              saving={savePrompt.isPending}
            />
          </>
        )}
      </div>

      {/* ---- Users ---- */}
      <div className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Users</h2>
        {(action.error || createUser.error) && (
          <p className="text-sm text-rose-300">
            {((action.error || createUser.error) as Error).message}
          </p>
        )}

        <Section title="Invite user" blurb="Cognito emails the new user a temporary password.">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-1">
              <span className="block text-xs uppercase tracking-wide text-slate-500">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="person@example.com"
              />
            </label>
            <label className="flex items-center gap-2 pb-1.5 text-sm text-slate-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-600 bg-slate-950"
                checked={newAdmin}
                onChange={(e) => setNewAdmin(e.target.checked)}
              />
              Admin
            </label>
            <button
              type="button"
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
              disabled={busy || !newEmail.includes("@")}
              onClick={() =>
                createUser.mutate(
                  { email: newEmail.trim(), is_admin: newAdmin },
                  { onSuccess: () => { setNewEmail(""); setNewAdmin(false); } },
                )
              }
            >
              Send invite
            </button>
          </div>
        </Section>

        <Section title="Accounts">
          {usersQ.isPending && <p className="text-sm text-slate-500">Loading users…</p>}
          {usersQ.error && <p className="text-sm text-rose-300">Error: {(usersQ.error as Error).message}</p>}
          {usersQ.data && (
            <div className="overflow-hidden rounded-lg border border-slate-800">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersQ.data.users.map((u) => {
                    const isMe = u.username === me;
                    return (
                      <tr key={u.username} className="border-b border-slate-800/60 last:border-0">
                        <td className="px-4 py-2 text-slate-200">
                          {u.email || u.username}
                          {isMe && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                        </td>
                        <td className="px-4 py-2">
                          <span className={u.enabled ? "text-emerald-400" : "text-rose-400"}>
                            {u.enabled ? "enabled" : "disabled"}
                          </span>
                          <span className="ml-2 text-xs text-slate-500">{u.status}</span>
                        </td>
                        <td className="px-4 py-2">
                          {u.is_admin ? <span className="text-amber-400">admin</span> : <span className="text-slate-400">user</span>}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-2">
                            <ActionBtn
                              label={u.is_admin ? "Revoke admin" : "Make admin"}
                              disabled={busy || (u.is_admin && isMe)}
                              onClick={() =>
                                action.mutate({
                                  path: `/admin/users/${enc(u.username)}/admin`,
                                  method: u.is_admin ? "DELETE" : "POST",
                                })
                              }
                            />
                            <ActionBtn
                              label={u.enabled ? "Disable" : "Enable"}
                              disabled={busy || (u.enabled && isMe)}
                              onClick={() =>
                                action.mutate({
                                  path: `/admin/users/${enc(u.username)}/${u.enabled ? "disable" : "enable"}`,
                                  method: "POST",
                                })
                              }
                            />
                            <ActionBtn
                              label="Delete"
                              danger
                              disabled={busy || isMe}
                              onClick={() => {
                                if (confirm(`Delete ${u.email || u.username}? This cannot be undone.`)) {
                                  action.mutate({ path: `/admin/users/${enc(u.username)}`, method: "DELETE" });
                                }
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function ActionBtn({
  label,
  onClick,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        "rounded-md border px-2.5 py-1 text-xs transition disabled:opacity-40 " +
        (danger
          ? "border-rose-800 text-rose-300 hover:bg-rose-950/40"
          : "border-slate-700 text-slate-300 hover:bg-slate-800")
      }
    >
      {label}
    </button>
  );
}
